import { FC } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import {
  Auth,
  Authors,
  ContentPerformance,
  CultureCategories,
  Cultures,
  Dashboard,
  DashboardV2,
  Languages,
  Quotes,
  References,
  Settings,
  Sources,
  SystemHealth,
  Tags,
  Users,
  Wrapper,
} from "./containers";
import UnauthorizedPage from "./containers/ErrorStates/403";
import NotFoundPage from "./containers/ErrorStates/404";
import { UserPermissions } from "./utils/permissions";

interface RouteConfig {
  path: string;
  Component: React.ComponentType;
  authenticate: boolean;
  requiredPermission?: UserPermissions;
}

const routes: RouteConfig[] = [
  {
    path: "/",
    Component: Auth,
    authenticate: false,
  },
  {
    path: "/users",
    Component: Users,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_USERS_LIST,
  },
  {
    path: "/cultures",
    Component: Cultures,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_CULTURES,
  },
  {
    path: "/culture-categories",
    Component: CultureCategories,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_CULTURE_CATEGORIES,
  },
  {
    path: "/quotes",
    Component: Quotes,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_QUOTES,
  },
  {
    path: "/tags",
    Component: Tags,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_TAGS,
  },
  {
    path: "/authors",
    Component: Authors,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_AUTHORS,
  },
  {
    path: "/references",
    Component: References,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_REFERENCES,
  },
  {
    path: "/sources",
    Component: Sources,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_SOURCES,
  },
  {
    path: "/languages",
    Component: Languages,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_PUBLISHED_LANGUAGES,
  },
  {
    path: "/dashboard",
    Component: DashboardV2,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_DASHBOARD,
  },
  {
    path: "/dashboard/legacy",
    Component: Dashboard,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_DASHBOARD,
  },
  {
    path: "/content-performance",
    Component: ContentPerformance,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_CONTENT_PERFORMANCE,
  },
  {
    path: "/settings",
    Component: Settings,
    authenticate: true,
    requiredPermission: UserPermissions.MANAGE_SETTINGS,
  },
  {
    path: "/system-health",
    Component: SystemHealth,
    authenticate: true,
    requiredPermission: UserPermissions.VIEW_SYSTEM_HEALTH,
  },
  {
    path: "/403",
    Component: UnauthorizedPage,
    authenticate: true,
  },
  {
    path: "*",
    Component: NotFoundPage,
    authenticate: true,
  },
];

const AppRoutes: FC = () => {
  const renderRouteElement = (route: RouteConfig) => {
    const { Component, authenticate, requiredPermission } = route;
    return (
      <ErrorBoundary>
        {authenticate ? (
          <Wrapper requiredPermission={requiredPermission}>
            <Component />
          </Wrapper>
        ) : (
          <Component />
        )}
      </ErrorBoundary>
    );
  };
  return (
    <BrowserRouter>
      <Routes>
        {routes.map(route => (
          <Route key={route.path} path={route.path} element={renderRouteElement(route)} />
        ))}
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
